/**
 * Hook for managing card number auto-increment functionality
 */

import { useState, useEffect, useCallback } from "react";
import { useStore } from "@/stores/useStore";
import {
  getNextCardNumber,
  updateCardNumberCounter,
} from "@/services/cardNumberCounter";
import { ManualCardContents } from "@/types/v2";

interface UseCardNumberAutoIncrementOptions {
  isCreating?: boolean; // Whether this is for creating a new card
  initialCardNumber?: string; // Initial card number value
}

export const useCardNumberAutoIncrement = (
  options: UseCardNumberAutoIncrementOptions = {}
) => {
  const { isCreating = false, initialCardNumber = "" } = options;
  const { shop } = useStore((s) => s);

  const [suggestedCardNumber, setSuggestedCardNumber] = useState<string>("");
  const [isAutoIncrementEnabled, setIsAutoIncrementEnabled] = useState(false);
  const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
  const [hasUserOverridden, setHasUserOverridden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if auto-increment is enabled in shop settings
  useEffect(() => {
    if (shop?.cardConfig?.cardContents) {
      const enabled =
        shop.cardConfig.cardContents[
          ManualCardContents.autoIncrementCardNumberEnabled
        ] || false;
      setIsAutoIncrementEnabled(enabled);
    }
  }, [shop]);

  // Fetch suggested card number when creating a new card and auto-increment is enabled
  useEffect(() => {
    if (
      isCreating &&
      isAutoIncrementEnabled &&
      shop?.id &&
      !hasUserOverridden
    ) {
      setIsFetchingSuggestion(true);
      setError(null);

      getNextCardNumber(shop.id)
        .then((nextNumber) => {
          setSuggestedCardNumber(nextNumber);
          setError(null);
        })
        .catch((error) => {
          console.error("Error fetching next card number:", error);
          setError("Failed to fetch suggested card number");
          setSuggestedCardNumber("1"); // Fallback
        })
        .finally(() => {
          setIsFetchingSuggestion(false);
        });
    }
  }, [isCreating, isAutoIncrementEnabled, shop?.id, hasUserOverridden]);

  // Handle manual card number changes
  const handleCardNumberChange = useCallback(
    (newCardNumber: string) => {
      // Clear any previous errors when user starts typing
      setError(null);

      // Mark as user overridden if the value differs from suggested
      if (newCardNumber !== suggestedCardNumber) {
        setHasUserOverridden(true);
      } else {
        setHasUserOverridden(false);
      }
    },
    [suggestedCardNumber]
  );

  // Update the counter when a card is saved
  const updateCounter = useCallback(
    async (cardNumber: string) => {
      if (shop?.id && cardNumber) {
        try {
          await updateCardNumberCounter(shop.id, cardNumber);
        } catch (error) {
          console.error("Error updating card number counter:", error);
        }
      }
    },
    [shop?.id]
  );

  // Reset the suggested card number (useful when creating multiple cards)
  const resetSuggestion = useCallback(() => {
    setHasUserOverridden(false);
    setSuggestedCardNumber("");

    if (isCreating && isAutoIncrementEnabled && shop?.id) {
      setIsFetchingSuggestion(true);

      getNextCardNumber(shop.id)
        .then((nextNumber) => {
          setSuggestedCardNumber(nextNumber);
        })
        .catch((error) => {
          console.error("Error fetching next card number:", error);
          setSuggestedCardNumber("1");
        })
        .finally(() => {
          setIsFetchingSuggestion(false);
        });
    }
  }, [isCreating, isAutoIncrementEnabled, shop?.id]);

  // Use suggested card number as initial value if creating and not overridden
  const getInitialCardNumber = useCallback(() => {
    if (
      isCreating &&
      isAutoIncrementEnabled &&
      suggestedCardNumber &&
      !hasUserOverridden
    ) {
      return suggestedCardNumber;
    }
    return initialCardNumber;
  }, [
    isCreating,
    isAutoIncrementEnabled,
    suggestedCardNumber,
    hasUserOverridden,
    initialCardNumber,
  ]);

  return {
    // State
    suggestedCardNumber,
    isAutoIncrementEnabled,
    isFetchingSuggestion,
    hasUserOverridden,
    error,

    // Actions
    handleCardNumberChange,
    updateCounter,
    resetSuggestion,
    getInitialCardNumber,

    // Utilities
    shouldShowSuggestion:
      isCreating && isAutoIncrementEnabled && !hasUserOverridden,
    isAutoSuggested:
      isCreating &&
      isAutoIncrementEnabled &&
      suggestedCardNumber &&
      !hasUserOverridden,
  };
};
